import React, { useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CheckCircle2, AlertTriangle, CalendarCheck, RefreshCw, 
  Cpu, Stethoscope, Map, BookOpen, ArrowRight, Layers, 
  Code2, Users, Megaphone, Settings, ChevronDown, ChevronUp,
  Ticket, Star, MailCheck, ShieldCheck, FileText, AlertOctagon,
  Download, Linkedin, Twitter, Copy
} from 'lucide-react';
import { Button } from '@dopomogai/ui';
import clsx from 'clsx';
import { LogicOutput } from '../engine/logic';
import { getAssessmentResult } from '../data/index';
import { Department, DynamicContentBlock } from '../engine/types';

type BlueprintResultProps = {
  calculation: LogicOutput;
  onRetake: () => void;
  onBook: (calParams?: string) => void;
  onArticleClick?: (slug: string) => void;
  userEmail: string; 
};

// --- DYNAMIC INVITATION CONFIG ---
type InviteVariant = {
  title: string;
  text: string;
  button: string;
  badge: string;
  color: 'amber' | 'blue' | 'rose';
};

const INVITE_VARIANTS: Record<string, InviteVariant> = {
  sovereign_founder: {
    title: "Strategic Architecture Session",
    text: "Your diagnostic indicates high readiness. We are inviting you to a private 45-minute blueprinting session with the founder to finalize your sovereign architecture.",
    button: "Claim Founding Partner Slot",
    badge: "Official Invitation",
    color: 'amber'
  },
  transformer_architect: {
    title: "Velocity Acceleration Session",
    text: "You have the signal, but you have friction. We invite you to a strategy session to map the specific agents that will remove your 'Human Router Tax'.",
    button: "Book Deployment Sprint",
    badge: "High Velocity",
    color: 'amber'
  },
  ai_native_founder: {
    title: "Platform Architecture Review",
    text: "You are building a complex engine. We invite you to a technical review session to validate your orchestration logic before you scale.",
    button: "Start POC Build",
    badge: "Technical Review",
    color: 'blue'
  },
  transformer_cleanup: {
    title: "Data Topology Audit",
    text: "Your potential is high, but your data structure is blocking automation. We invite you to a forensic audit session to map your 'Messy Middle' before you build.",
    button: "Secure Audit Session",
    badge: "Forensic Audit",
    color: 'blue'
  },
  in_app_concierge: {
    title: "Integration Strategy Call",
    text: "Deep integration requires careful planning. We invite you to an architecture audit to define the safest path for injecting intelligence into your UI.",
    button: "Book Architecture Audit",
    badge: "Technical Audit",
    color: 'blue'
  },
  feature_injection: {
    title: "Data Pipeline Strategy",
    text: "Unlock your data without breaking your product. We invite you to map the RAG pipelines required to make your feature intelligent.",
    button: "Book Data Audit",
    badge: "Strategy Session",
    color: 'blue'
  },
  transformer_digital: {
    title: "Digital Transition Strategy",
    text: "You cannot automate what you cannot see. We invite you to a consultation to design the 'Sidecar Systems' that will digitize your operations safely.",
    button: "Discuss Transition Path",
    badge: "Transition Strategy",
    color: 'rose'
  },
  traditional_builder: {
    title: "Organizational Pivot Session",
    text: "Your current hiring plan carries long-term process debt. We invite you to a strategy call to discuss how to pivot toward a Lean, AI-First model.",
    button: "Discuss Lean Scaling",
    badge: "Pivot Strategy",
    color: 'rose'
  },
  wrapper_warning: {
    title: "Defensibility Strategy Call",
    text: "Your current model is at risk of commoditization. We invite you to a product strategy session to define a defensible 'Workflow Moat'.",
    button: "Book Pivot Strategy",
    badge: "Risk Mitigation",
    color: 'rose'
  },
  bootstrapper: {
    title: "Founder Guidance Call",
    text: "You are in survival mode. We invite you to a brief chat to discuss the 'Manual AI' stack that extends your runway without engineering cost.",
    button: "Get Founder Guides",
    badge: "Founder Support",
    color: 'blue'
  }
};

// --- SUB-COMPONENTS ---

const RoadmapBlock = ({ block, isMain = false }: { block: DynamicContentBlock, isMain?: boolean }) => (
  <div className={clsx("relative", !isMain && "pl-4 border-l-2 border-slate-100")}>
    <div className="flex items-center gap-2 mb-6 text-slate-900 border-b border-slate-200 pb-3">
      <Map size={18} className={isMain ? "text-emerald-600" : "text-slate-400"} />
      <h3 className="text-xs font-black uppercase tracking-widest">
        {block.roadmap_title}
      </h3>
    </div>
    <div className="grid grid-cols-1 gap-6">
      {block.steps.map((step, idx) => (
        <div key={idx} className="flex gap-4 items-start relative">
          {idx !== block.steps.length - 1 && (
            <div className="absolute left-[11px] top-8 bottom-[-24px] w-0.5 bg-slate-200" />
          )}
          <div className={clsx(
            "w-6 h-6 rounded-full text-white flex items-center justify-center text-xs font-bold shrink-0 mt-1 shadow-md z-10",
            isMain ? "bg-slate-900" : "bg-slate-400"
          )}>
            {idx + 1}
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className={clsx(
                "text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded",
                isMain ? "text-emerald-600 bg-emerald-50" : "text-slate-500 bg-slate-100"
              )}>
                {step.phase}
              </span>
            </div>
            <h4 className="font-bold text-slate-900 text-base mb-1">{step.name}</h4>
            <p className="text-slate-600 text-sm leading-relaxed">{step.detail}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const AlternativeDepartment = ({ deptKey, block }: { deptKey: string, block: DynamicContentBlock }) => {
  const [isOpen, setIsOpen] = useState(false);
  const icons: Record<string, React.ReactNode> = {
    engineering: <Code2 size={18} />,
    sales: <Users size={18} />,
    marketing: <Megaphone size={18} />,
    ops: <Settings size={18} />
  };
  const labels: Record<string, string> = {
    engineering: "Engineering Track",
    sales: "Sales Track",
    marketing: "Marketing Track",
    ops: "Operations Track"
  };

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white mb-3 transition-all hover:border-slate-300 no-print">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <div className="text-slate-400">
             {icons[deptKey] || <Layers size={18} />}
          </div>
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-slate-500 block">
              {labels[deptKey] || deptKey}
            </span>
            <span className="font-bold text-slate-900 text-sm">{block.title}</span>
          </div>
        </div>
        {isOpen ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="p-6 bg-white border-t border-slate-100">
              <p className="text-sm font-medium text-slate-600 mb-6 italic border-l-2 border-emerald-500 pl-3">
                "{block.objective}"
              </p>
              <RoadmapBlock block={block} isMain={false} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// 3. ALTERNATIVE RESULT CARD
const AlternativeResultCard = ({ id, onClick }: { id: string; onClick: () => void }) => {
  const result = getAssessmentResult(id);
  if (!result) return null;
  return (
    <div 
      onClick={onClick} 
      className="group cursor-pointer relative bg-slate-800/40 border border-slate-700 rounded-xl p-6 hover:bg-slate-800 hover:border-emerald-500/50 transition-all duration-300 no-print"
    >
      <div className="absolute inset-0 bg-emerald-500/0 group-hover:bg-emerald-500/5 rounded-xl transition-colors duration-300" />
      <div className="relative z-10">
        <div className="flex justify-between items-center mb-3">
          <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-400">
            Alternative Outcome
          </span>
          <ArrowRight 
            size={16} 
            className="text-slate-500 group-hover:text-emerald-400 transition-all duration-300 group-hover:translate-x-1" 
          />
        </div>
        <h4 className="font-bold text-white text-lg mb-2">
          {result.title}
        </h4>
        <p className="text-sm text-slate-400 leading-relaxed line-clamp-2">
          {result.diagnosis.headline}
        </p>
      </div>
    </div>
  );
};

// 4. FOUNDER INVITATION
const FounderInvitation = ({ onBook, email, variant }: { onBook: () => void, email: string, variant: InviteVariant }) => {
  const styles = {
    amber: {
      gradient: "from-amber-300 via-yellow-500 to-amber-300",
      glow: "bg-amber-500/20",
      iconBg: "bg-amber-500/10 border-amber-500/30",
      iconColor: "text-amber-400",
      btn: "bg-amber-500 hover:bg-amber-600 text-slate-900 border-amber-600",
      title: "text-white"
    },
    blue: {
      gradient: "from-blue-400 via-indigo-500 to-blue-400",
      glow: "bg-blue-500/20",
      iconBg: "bg-blue-500/10 border-blue-500/30",
      iconColor: "text-blue-400",
      btn: "bg-blue-500 hover:bg-blue-600 text-white border-blue-600",
      title: "text-white"
    },
    rose: {
      gradient: "from-rose-400 via-red-500 to-rose-400",
      glow: "bg-rose-500/20",
      iconBg: "bg-rose-500/10 border-rose-500/30",
      iconColor: "text-rose-400",
      btn: "bg-rose-600 hover:bg-rose-700 text-white border-rose-700 shadow-rose-900/20",
      title: "text-rose-50"
    }
  };

  const style = styles[variant.color];

  return (
    <div className="bg-slate-900 rounded-xl p-1 relative overflow-hidden shadow-2xl no-print">
      <div className={clsx("absolute top-0 left-0 right-0 h-1 bg-gradient-to-r", style.gradient)} />
      <div className={clsx("absolute -top-20 -right-20 w-40 h-40 blur-3xl rounded-full pointer-events-none", style.glow)} />

      <div className="bg-slate-900 rounded-lg p-6 md:p-8 relative z-10 text-center md:text-left flex flex-col md:flex-row items-center gap-8">
        <div className="flex-shrink-0">
          <div className={clsx("w-16 h-16 rounded-full flex items-center justify-center border", style.iconBg)}>
            {variant.color === 'rose' ? <AlertOctagon className={style.iconColor} size={32} /> : <Ticket className={style.iconColor} size={32} />}
          </div>
        </div>

        <div className="flex-grow">
          <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
            <Star className={clsx("fill-current", style.iconColor)} size={12} />
            <span className={clsx("text-[10px] font-bold uppercase tracking-[0.2em]", style.iconColor)}>
              {variant.badge}
            </span>
            <Star className={clsx("fill-current", style.iconColor)} size={12} />
          </div>
          
          <h3 className={clsx("text-2xl font-black mb-2 tracking-tight", style.title)}>
            {variant.title}
          </h3>
          
          <p className="text-slate-400 text-sm leading-relaxed mb-6 md:mb-4 max-w-xl">
            {variant.text}
          </p>

          <div className="flex flex-col md:flex-row gap-4 items-center">
            <Button
              variant="primary"
              size="lg"
              className={clsx("w-full md:w-auto font-black shadow-lg", style.btn)}
              text={variant.button}
              icon={<CalendarCheck size={18} />}
              onClick={onBook}
            />
            {email && (
              <div className="text-[10px] text-slate-500 font-mono">
                // INVITE SENT TO: <span className="text-slate-300">{email}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// --- HELPER: SHARING & PRINTING ---
            {/* Status Bar 

const ShareToolbar = ({ resultTitle, verdict }: { resultTitle: string, verdict: string }) => {
  const [copied, setCopied] = useState(false);
  
  const handlePrint = () => {
    window.print();
  };

  const handleShareTwitter = () => {
    const text = `I just generated my AI Operations Blueprint: ${resultTitle} (${verdict}). \n\nGet your free architectural diagnosis here:`;
    const url = window.location.href; // Uses current page URL
    const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    window.open(tweetUrl, '_blank', 'width=550,height=420');
  };

  const handleShareLinkedIn = () => {
    const url = window.location.href;
    const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    window.open(linkedinUrl, '_blank', 'width=550,height=520');
  };
  
  const handleCopyLink = () => {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-center gap-2 no-print mb-6 md:mb-0 md:absolute md:top-8 md:right-8 z-20">
      <button 
        onClick={handlePrint}
        className="flex items-center gap-2 px-3 py-2 bg-slate-50 hover:bg-slate-100 text-slate-600 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors border border-slate-200"
        title="Download PDF Report"
      >
        <Download size={16} /> <span className="hidden sm:inline">PDF</span>
      </button>
      
      <div className="h-6 w-px bg-slate-200 mx-1" />
      
      <button 
        onClick={handleShareTwitter}
        className="p-2 text-slate-400 hover:text-[#1DA1F2] hover:bg-blue-50 rounded-lg transition-colors"
        title="Share on Twitter"
      >
        <Twitter size={18} />
      </button>
      
      <button 
        onClick={handleShareLinkedIn}
        className="p-2 text-slate-400 hover:text-[#0A66C2] hover:bg-blue-50 rounded-lg transition-colors"
        title="Share on LinkedIn"
      >
        <Linkedin size={18} />
      </button>

      <button 
        onClick={handleCopyLink}
        className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors relative"
        title="Copy Link"
      >
        {copied ? <CheckCircle2 size={18} className="text-emerald-600" /> : <Copy size={18} />}
      </button>
    </div>
  );
};
*/}

// --- MAIN COMPONENT ---

export const BlueprintResult: React.FC<BlueprintResultProps> = ({ 
  calculation, 
  onRetake, 
  onBook, 
  onArticleClick,
  userEmail
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [viewId, setViewId] = useState(calculation.primaryResultId);
  const isPreview = viewId !== calculation.primaryResultId;

  // 1. DATA LOOKUP
  const result = getAssessmentResult(viewId);
  if (!result) return <div className="p-8 text-center text-red-500">Error: Result data not found.</div>;

  // 2. CONTEXT RESOLUTION
  const selectedDept = (calculation.context.department || 'default') as string;
  
  // 3. DYNAMIC CONTENT
  const mainBlock = result.dynamic_content[selectedDept as Department] || result.dynamic_content['default'];
  const otherDepts = Object.entries(result.dynamic_content)
    .filter(([key, val]) => key !== selectedDept && key !== 'default' && val.steps.length > 0);

  // 4. INVITE VARIANT
  const inviteVariant = INVITE_VARIANTS[viewId] || INVITE_VARIANTS['sovereign_founder'];

  // Scroll Logic
  useEffect(() => {
    if (containerRef.current) {
        const yOffset = -100; 
        const y = containerRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }, [viewId]);

  const isSuccess = ['book', 'start_poc'].includes(result.cta.primary.variant);

  return (
    <div ref={containerRef} className="max-w-4xl mx-auto w-full font-sans scroll-mt-32">
        {/* PRINT STYLES - Only applied during print/pdf generation */}
        <style>{`
          @media print {
            body {
              visibility: hidden;
              background-color: white;
            }
            #blueprint-result-card {
              visibility: visible;
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              margin: 0 !important;
              padding: 0 !important;
              border: none !important;
              box-shadow: none !important;
            }
            .no-print, header, footer, #assessment > div > div:first-child {
              display: none !important;
            }
          }
        `}</style>

        <motion.div 
            id="blueprint-result-card"
            key={viewId}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="bg-white border-2 border-slate-900 rounded-2xl shadow-[8px_8px_0_0_#10b981] overflow-hidden relative flex flex-col mb-12"
        >
            {/* Preview Banner */}
            {isPreview && (
              <div 
                className="bg-slate-900 text-white text-xs font-bold uppercase tracking-widest py-3 text-center cursor-pointer hover:bg-slate-800 transition-colors z-30 relative no-print" 
                onClick={() => setViewId(calculation.primaryResultId)}
              >
                Viewing alternative path. Tap here to return to your result.
              </div>
            )}

            {/* Status Bar */}
            <div className={clsx("h-2 w-full border-b-2 border-slate-900 z-20 no-print", isSuccess ? "bg-emerald-500" : "bg-amber-500")} />

            {/* === HERO IMAGE SECTION === */}
            <div className="w-full aspect-video bg-slate-900 border-b-2 border-slate-100 relative overflow-hidden group">
                 {/* Visual Fix: Ensure no padding, object-cover fills space */}
                 <div className="absolute inset-0 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:20px_20px] opacity-10 pointer-events-none" />
                 
                 <div className={clsx(
                    "absolute top-6 right-6 z-30 px-3 py-1.5 bg-white/90 backdrop-blur border-2 rounded-lg font-black uppercase text-xs tracking-widest shadow-sm transform rotate-2 no-print",
                    isSuccess ? "border-emerald-500 text-emerald-700" : "border-amber-500 text-amber-700"
                 )}>
                    {isSuccess ? "QUALIFIED" : "AUDIT REQ"}
                 </div>

                 <img 
                    src={result.hero_image}
                    alt={result.title}
                    // IMPORTANT: object-cover + w-full + h-full removes whitespace
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                 />
            </div>

            {/* === CONTENT BODY === */}
            <div className="p-6 md:p-10 bg-white relative z-10">
                
                {/* Header with Share Toolbar */}
                <div className="relative flex flex-col gap-3 mb-8 border-b-2 border-slate-100 pb-8">
                    
                    {/* Share Buttons 
                    <ShareToolbar resultTitle={result.title} verdict={result.verdict} />*/}

                    <div className="flex items-center gap-2 mb-1">
                        {isSuccess ? <CheckCircle2 className="text-emerald-600" size={18} /> : <AlertTriangle className="text-amber-500" size={18} />}
                        <span className="font-mono text-xs font-bold uppercase tracking-widest text-slate-500">
                            {result.verdict}
                        </span>
                    </div>
                    <h2 className="text-3xl md:text-4xl font-black text-slate-900 uppercase tracking-tight leading-[0.95] max-w-2xl">
                        {result.title}
                    </h2>
                    <p className="text-lg text-slate-600 font-medium leading-relaxed mt-2 max-w-2xl">
                        {result.diagnosis.headline}
                    </p>
                </div>

                {/* Diagnosis */}
                <div className="mb-10">
                    <div className="flex items-center gap-2 mb-3 text-slate-400">
                        <Stethoscope size={18} />
                        <h3 className="text-xs font-black uppercase tracking-widest">The Diagnosis</h3>
                    </div>
                    <p className="text-slate-700 text-base leading-relaxed border-l-4 border-slate-200 pl-4 py-1">
                        {result.diagnosis.text}
                    </p>
                </div>

                {/* Solution */}
                <div className="mb-12 bg-slate-50 border border-slate-200 rounded-xl p-6 print:border print:bg-white">
                    <div className="flex items-center gap-2 mb-3 text-violet-600">
                        <Cpu size={20} />
                        <h3 className="text-xs font-black uppercase tracking-widest">The Solution: {result.solution.headline}</h3>
                    </div>
                    <p className="text-slate-800 text-base font-medium leading-relaxed">
                            {result.solution.text.split('**').map((part, i) => 
                            i % 2 === 1 ? <strong key={i} className="text-slate-900">{part}</strong> : part
                            )}
                    </p>
                </div>

                {/* Main Roadmap */}
                {mainBlock && mainBlock.steps.length > 0 && (
                    <div className="mb-12 print:break-inside-avoid">
                        <div className="bg-emerald-50/50 rounded-2xl p-6 border border-emerald-100 print:bg-white print:border-slate-200">
                            <h3 className="text-emerald-800 font-black text-lg mb-2 print:text-slate-900">{mainBlock.title}</h3>
                            <p className="text-emerald-700 text-sm mb-6 font-medium print:text-slate-600">{mainBlock.objective}</p>
                            <RoadmapBlock block={mainBlock} isMain={true} />
                        </div>
                    </div>
                )}

                {/* Alternative Departments (Hidden on Print) */}
                {otherDepts.length > 0 && (
                    <div className="mb-12 no-print">
                        <div className="flex items-center gap-2 mb-4 text-slate-400 border-b border-slate-100 pb-2">
                            <Layers size={16} />
                            <h3 className="text-xs font-black uppercase tracking-widest">Explore Other Department Tracks</h3>
                        </div>
                        <div className="space-y-3">
                            {otherDepts.map(([key, block]) => (
                                <AlternativeDepartment key={key} deptKey={key} block={block} />
                            ))}
                        </div>
                    </div>
                )}

                {/* Articles */}
                {result.intelligence.articles.length > 0 && (
                    <div className="mb-16 print:break-inside-avoid">
                            <div className="flex items-center gap-2 mb-6 text-slate-400">
                            <BookOpen size={18} />
                            <h3 className="text-xs font-black uppercase tracking-widest">{result.intelligence.title}</h3>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {result.intelligence.articles.map((article, idx) => (
                                <a 
                                    key={idx} 
                                    href={`/blog/${article.slug}`}
                                    onClick={(e) => {
                                        if (onArticleClick) { e.preventDefault(); onArticleClick(article.slug); }
                                    }}
                                    className="group block p-4 rounded-xl border-2 border-slate-100 hover:border-emerald-500 hover:bg-emerald-50/30 transition-all cursor-pointer no-underline text-slate-900"
                                >
                                    <h4 className="font-bold text-slate-900 text-sm mb-1 group-hover:text-emerald-700">{article.title}</h4>
                                    <p className="text-xs text-slate-500 line-clamp-2">{article.subtitle}</p>
                                </a>
                            ))}
                        </div>
                    </div>
                )}

                {/* ACTION AREA (Invitation) - Hidden on print */}
                <div className="border-t-2 border-slate-100 pt-10 pb-4 no-print">
                    
                    <FounderInvitation 
                        email={userEmail} 
                        variant={inviteVariant}
                        onBook={() => onBook(JSON.stringify(result.cta.primary.cal_params))} 
                    />

                    <div className="flex flex-col md:flex-row items-center justify-center gap-4 mt-8 text-slate-400">
                        {userEmail && (
                            <div className="flex items-center gap-2 text-xs font-medium">
                                <MailCheck size={14} className="text-emerald-600" />
                                <span>Blueprint sent to inbox</span>
                            </div>
                        )}
                        <div className="hidden md:block w-1 h-1 bg-slate-300 rounded-full" />
                        <button 
                            onClick={onRetake} 
                            className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider hover:text-slate-600 transition-colors"
                        >
                            <RefreshCw size={14} /> Start Over
                        </button>
                    </div>
                </div>

            </div>
        </motion.div>

        {/* Footer: Explore Totally Different Paths (Hidden on Print) */}
        {calculation.relatedResultIds.length > 0 && !isPreview && (
            <div className="mt-16 border-t border-slate-800 pt-12 pb-24 no-print">
                <div className="text-center mb-10">
                    <h3 className="text-slate-500 font-bold uppercase tracking-widest text-xs mb-3">The Operations Spectrum</h3>
                    <p className="text-white text-2xl font-bold tracking-tight">Explore other potential architectures</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-4">
                    {calculation.relatedResultIds.map(id => (
                        <AlternativeResultCard 
                          key={id} 
                          id={id} 
                          onClick={() => setViewId(id)} 
                        />
                    ))}
                </div>
            </div>
        )}
    </div>
  );
};